import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type {
  CapacityManagementRootResponse,
  CorrectionsParams,
  CorrectionsResponse,
} from 'src/apis/capacity-management/types';
import type { CapacityManagementRootRequest } from 'src/apis/capacity-management/use-capacity-management-root';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookConfig = {
  capacityManagementRoot: CapacityManagementRootRequest;
  params: CorrectionsParams;
};

type RequestOptions = {
  rootData: CapacityManagementRootResponse;
  params: CorrectionsParams;
};

export function useCorrections({ capacityManagementRoot, params }: HookConfig) {
  const corrections = useAxios(
    (axios, baseConfig, { rootData, params }: RequestOptions) => {
      if (!rootData._links?.corrections) throw new Forbidden();

      const url = urlTemplate
        .parse(rootData._links.corrections.href)
        .expand({});

      return axios.request<CorrectionsResponse>({
        ...baseConfig,
        url,
        params,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!capacityManagementRoot.response) return;

    const rootData = capacityManagementRoot.response.data;
    const options = { rootData, params };

    corrections.execute(options);
    // oxlint-disable-next-line exhaustive-deps
  }, [capacityManagementRoot.response, params]);

  return corrections;
}

export type CorrectionsRequest = ReturnType<typeof useCorrections>;
